import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Avatar from 'flarum/common/components/Avatar';
import Tooltip from 'flarum/common/components/Tooltip';
import Dropdown from 'flarum/common/components/Dropdown';
import DiscussionControls from 'flarum/forum/utils/DiscussionControls';
import { trans, numberOr, discussionRoute, tagRoute, displayName, formatTimeLabel, postPreview, tagPillStyle, truncate, navigate, userRoute, highlight } from '../../utils';
import { toggleBookmark, isBookmarked, bookmarkNote, bookmarkRemindAt, bookmarksEnabled, avocadoBookmarksEnabled, bookmarkActionLabel } from '../../utils/bookmarks';
import { formatDateTime } from '../../utils/clock';
import BookmarkModal from '../BookmarkModal';
import UserHoverCard from './UserHoverCard';
import CakedayBadge from './CakedayBadge';
export default class ThreadCard extends Component {
  view() {
    const {
      discussion,
      context,
      likingIds,
      onToggleLike,
      updatedLikeIds = new Set(),
      newDiscIds = new Set(),
      currentTag = null,
      searchQuery = '',
      filterTagIds,
      variant = 'home',
      showBookmarkMeta = false
    } = this.attrs;
    if (!discussion) return null;
    const id = discussion.id?.();
    const user = discussion.user?.();
    const title = discussion.title?.() || 'Untitled';
    const href = discussionRoute(discussion);
    const allTags = (discussion.tags?.() || []).filter(Boolean);
    const tags = filterTagIds ? allTags.filter(t => !filterTagIds.has(String(t.id?.() ?? ''))) : allTags;
    const isSticky = discussion.isSticky?.() || false;
    const isLocked = discussion.isLocked?.() || false;
    const isFollowing = discussion.subscription?.() === 'follow';
    const isUnread = discussion.isUnread?.() || false;
    const unreadCount = isUnread ? numberOr(discussion.unreadCount?.(), 0) : 0;
    const replies = numberOr(discussion.replyCount?.(), 0);
    const timeLabel = formatTimeLabel(discussion.lastPostedAt?.());
    const userProfileHref = userRoute(user);
    const isNew = newDiscIds.has(id);
    const isSearch = variant === 'search';
    const p = isSearch ? 'AvocadoSearch' : 'AvocadoHome';

    // ── excerpt ──────────────────────────────────────────────────────────────
    const excerptRaw = isSearch ? (() => {
      try {
        const mrp = discussion.mostRelevantPost?.();
        if (mrp) return mrp.contentPlain?.() || '';
        return discussion.firstPost?.()?.contentPlain?.() || '';
      } catch {
        return '';
      }
    })() : postPreview(discussion);
    const excerptNode = isSearch ? searchQuery ? highlight(excerptRaw, searchQuery, 160) : truncate(excerptRaw, 160) : excerptRaw;
    const titleNode = isSearch && searchQuery ? highlight(title, searchQuery) : title;

    // ── controls dropdown ────────────────────────────────────────────────────
    const controls = DiscussionControls.controls(discussion, context).toArray();
    const controlsDropdown = controls.length ? m(Dropdown, {
      className: "AvocadoHome-threadControls",
      icon: "fas fa-ellipsis-v",
      buttonClassName: "Button Button--icon Button--flat AvocadoHome-threadControls-toggle",
      accessibleToggleLabel: app.translator.trans('core.forum.discussion_controls.toggle_dropdown_accessible_label')
    }, controls) : null;

    // ── bookmark button ───────────────────────────────────────────────────────
    // Sistema do tema — não salvo: um clique salva; salvo: o clique abre o modal
    // de nota/lembrete (remover vive dentro do modal e no dropdown de controles).
    // Cedendo ao fof/bookmarks não há nota nem lembrete, então o botão vira um
    // toggle puro que grava na extensão dele.
    const editsNoteAndReminder = avocadoBookmarksEnabled();
    const saved = isBookmarked(discussion);
    const bookmarkLabel = saved && editsNoteAndReminder ? trans('ramon-avocado.forum.bookmarks.edit', 'Edit bookmark') : bookmarkActionLabel(saved);
    const bookmarkBtn = app.session.user && bookmarksEnabled() ? m(Tooltip, {
      text: bookmarkLabel,
      position: "top"
    }, m("button", {
      className: `AvocadoThreadCard-bookmarkBtn${saved ? ' is-saved' : ''}`,
      type: "button",
      "aria-pressed": saved,
      "aria-label": bookmarkLabel,
      onclick: e => {
        e.stopPropagation();
        e.preventDefault();
        // O Tooltip também dispara por focus: sem o blur ele ficava preso
        // depois do clique, flutuando após o botão esconder (hover-only).
        e.currentTarget?.blur();
        if (saved && editsNoteAndReminder) {
          app.modal.show(BookmarkModal, {
            discussion
          });
        } else {
          toggleBookmark(discussion);
        }
      }
    }, m("i", {
      className: saved ? 'fas fa-bookmark' : 'far fa-bookmark',
      "aria-hidden": "true"
    }))) : null;

    // ── bookmark meta (BookmarksPage only) ────────────────────────────────────
    const metaVisible = showBookmarkMeta && saved && editsNoteAndReminder;
    const note = metaVisible ? bookmarkNote(discussion) : '';
    const remindAt = metaVisible ? bookmarkRemindAt(discussion) : null;
    const bookmarkMeta = note || remindAt ? m("button", {
      className: "AvocadoBookmark-meta",
      type: "button",
      onclick: e => {
        e.stopPropagation();
        e.preventDefault();
        app.modal.show(BookmarkModal, {
          discussion
        });
      }
    }, remindAt && m("span", {
      className: "AvocadoBookmark-metaReminder"
    }, m("i", {
      className: "far fa-clock",
      "aria-hidden": "true"
    }), formatDateTime(remindAt)), note && m("span", {
      className: "AvocadoBookmark-metaNote"
    }, m("i", {
      className: "far fa-sticky-note",
      "aria-hidden": "true"
    }), truncate(note, 120))) : null;

    // ── like state (home variant) ─────────────────────────────────────────────
    const likes = numberOr(discussion.firstPost?.()?.attribute?.('likesCount'), 0);
    const isLiked = !!(app.session.user && (discussion.firstPost?.()?.likes?.() || []).some(u => u === app.session.user));
    const isLiking = likingIds.has(id);

    // ── reply card (home variant, only when there are replies) ────────────────
    const replyCardNode = !isSearch && replies > 0 ? (() => {
      const lastPoster = discussion.lastPostedUser?.();
      const lastPost = discussion.lastPost?.();
      if (!lastPoster && !lastPost) return null;
      const preview = truncate(lastPost?.contentPlain?.() || '', 100);
      const otherCount = replies - 1;
      const lastPostHref = (() => {
        try {
          const n = discussion.lastPostNumber?.();
          return n ? app.route.discussion(discussion, n) : href;
        } catch {
          return href;
        }
      })();
      const secondHref = (() => {
        try {
          return app.route.discussion(discussion, 2);
        } catch {
          return href;
        }
      })();
      return m("div", {
        className: "AvocadoHome-replyCard"
      }, m("a", {
        className: "AvocadoHome-replyCard-line",
        href: lastPostHref,
        onclick: e => {
          e.stopPropagation();
          navigate(e, lastPostHref);
        }
      }, m("div", {
        className: "AvocadoHome-replyCard-avatar"
      }, lastPoster && m(Avatar, {
        user: lastPoster
      })), m("span", {
        className: "AvocadoHome-replyCard-name"
      }, displayName(lastPoster)), preview && m("span", {
        className: "AvocadoHome-replyCard-text"
      }, preview)), otherCount > 0 && m("a", {
        className: "AvocadoHome-replyCard-seeMore",
        href: secondHref,
        onclick: e => {
          e.stopPropagation();
          navigate(e, secondHref);
        }
      }, otherCount === 1 ? trans('ramon-avocado.forum.home.see_other_reply_singular', 'See other {count} reply', {
        count: otherCount
      }) : trans('ramon-avocado.forum.home.see_other_replies', 'See other {count} replies', {
        count: otherCount
      })));
    })() : null;
    const primaryTagColor = (allTags.length ? allTags[0].color?.() : null) || 'var(--primary-color)';
    return m("article", {
      key: id,
      className: `${p}-threadCard${isUnread ? ` ${p}-threadCard--unread` : ''}${isNew ? ` ${p}-threadCard--new` : ''}`,
      style: {
        '--item-tag-color': primaryTagColor
      }
    }, m("div", {
      className: `${p}-threadHead`
    }, m("div", {
      className: isSearch ? `${p}-threadAvatar` : 'AvocadoHome-avatarWrap'
    }, user && m(UserHoverCard, {
      user: user
    }, m(Avatar, {
      user: user,
      title: displayName(user)
    })), unreadCount > 0 && m(Tooltip, {
      text: trans('ramon-avocado.forum.home.badge_unread_tooltip', unreadCount === 1 ? '1 unread post' : '{count} unread posts', {
        count: unreadCount
      }),
      position: "top"
    }, m("span", {
      className: "AvocadoHome-unreadBadge",
      "aria-hidden": "true"
    }, unreadCount > 99 ? '99+' : unreadCount))), m("div", {
      className: `${p}-threadMain`
    }, m("div", {
      className: `${p}-threadMeta`
    }, m(UserHoverCard, {
      user: user
    }, m("a", {
      className: `${p}-threadAuthor`,
      href: userProfileHref,
      onclick: e => {
        e.stopPropagation();
        navigate(e, userProfileHref);
      }
    }, displayName(user))), user && m(CakedayBadge, {
      user: user
    }), timeLabel && m("span", {
      className: `${p}-threadTime`
    }, timeLabel), isNew && m("span", {
      className: "AvocadoStatDot AvocadoStatDot--new",
      "aria-hidden": "true"
    }), isSticky && m(Tooltip, {
      text: trans('ramon-avocado.forum.home.badge_sticky', 'Pinned'),
      position: "top"
    }, m("span", {
      className: "AvocadoHome-badge AvocadoHome-badge--sticky"
    }, m("i", {
      className: "fas fa-thumbtack",
      "aria-hidden": "true"
    }))), isLocked && m(Tooltip, {
      text: app.translator.trans('flarum-lock.forum.badge.locked_tooltip'),
      position: "top"
    }, m("span", {
      className: "AvocadoHome-badge AvocadoHome-badge--locked"
    }, m("i", {
      className: "fas fa-lock",
      "aria-hidden": "true"
    }))), isFollowing && m(Tooltip, {
      text: trans('ramon-avocado.forum.home.badge_following', 'Following'),
      position: "top"
    }, m("span", {
      className: "AvocadoHome-badge AvocadoHome-badge--following"
    }, m("i", {
      className: "fas fa-star",
      "aria-hidden": "true"
    }))), tags.slice(0, 4).map((tag, idx) => {
      const tagColor = tag.color?.() || null;
      const isCurrentTag = currentTag && tag.id?.() === currentTag.id?.();
      const extraClass = idx >= 2 ? ' AvocadoHome-tagPill--extra' : '';
      const tagStyle = tagPillStyle(tagColor);
      if (isCurrentTag) {
        return m("span", {
          key: tag.id?.(),
          className: `AvocadoHome-tagPill${extraClass}`,
          style: {
            ...tagStyle,
            cursor: 'default'
          }
        }, tag.icon?.() && m("i", {
          className: tag.icon(),
          "aria-hidden": "true"
        }), tag.name?.());
      }
      return m("a", {
        key: tag.id?.(),
        className: `AvocadoHome-tagPill${extraClass}`,
        href: tagRoute(tag),
        onclick: e => {
          e.stopPropagation();
          navigate(e, tagRoute(tag));
        },
        style: tagStyle
      }, tag.icon?.() && m("i", {
        className: tag.icon(),
        "aria-hidden": "true"
      }), tag.name?.());
    }), tags.length > 2 && m("span", {
      className: "AvocadoHome-tagMore"
    }, "+", tags.length - 2)), m("a", {
      className: `${p}-threadTitle`,
      href: href,
      onclick: e => navigate(e, href)
    }, titleNode), excerptNode && m("p", {
      className: `${p}-threadExcerpt`
    }, excerptNode), bookmarkMeta, isSearch && m("div", {
      className: `${p}-threadFooter`
    }, m("span", {
      className: `${p}-threadReplies`
    }, m("i", {
      className: "far fa-comment",
      "aria-hidden": "true"
    }), replies === 1 ? trans('ramon-avocado.forum.home.reply_singular', '1 reply') : trans('ramon-avocado.forum.home.reply_plural', '{count} replies', {
      count: replies
    })))), isSearch ? m("div", {
      className: "AvocadoSearch-threadActions"
    }, bookmarkBtn, controlsDropdown) : m("div", {
      className: "AvocadoHome-threadActions"
    }, bookmarkBtn, controlsDropdown, m("button", {
      className: "AvocadoHome-replyBtn",
      onclick: e => {
        e.stopPropagation();
        if (!app.session.user) {
          app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'));
          return;
        }
        app.composer.load(() => flarum.reg.asyncModuleImport('flarum/forum/components/ReplyComposer'), {
          user: app.session.user,
          discussion
        }).then(() => {
          app.composer.show();
          m.route.set(href);
        });
      }
    }, m("i", {
      className: "fas fa-reply",
      "aria-hidden": "true"
    }), trans('ramon-avocado.forum.home.reply_label', 'Reply')))), replyCardNode && m("div", {
      className: "AvocadoHome-threadReplyGroup"
    }, replyCardNode), !isSearch && m("div", {
      className: "AvocadoHome-threadStats"
    }, m("button", {
      className: ['AvocadoHome-statBtn AvocadoHome-statBtn--likes', isLiked ? ' AvocadoHome-statBtn--liked' : '', isLiking ? ' AvocadoHome-statBtn--loading' : '', updatedLikeIds.has(id) ? ' AvocadoHome-statBtn--pop' : ''].join(''),
      onclick: e => {
        e.stopPropagation();
        if (!app.session.user) {
          app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'));
          return;
        }
        onToggleLike(discussion);
      },
      title: trans('ramon-avocado.forum.home.like', 'Like')
    }, m("i", {
      className: isLiked ? 'fas fa-thumbs-up' : 'far fa-thumbs-up',
      "aria-hidden": "true"
    }), m("span", null, likes === 1 ? trans('ramon-avocado.forum.home.like_count_singular', '1 like') : trans('ramon-avocado.forum.home.like_count_plural', '{count} likes', {
      count: likes
    }))), m("button", {
      className: "AvocadoHome-statBtn AvocadoHome-statBtn--replies",
      onclick: e => {
        e.stopPropagation();
        m.route.set(href);
      },
      title: trans('ramon-avocado.forum.home.replies', 'Replies')
    }, m("i", {
      className: "far fa-comment",
      "aria-hidden": "true"
    }), m("span", null, replies === 1 ? trans('ramon-avocado.forum.home.reply_count_singular', '1 resposta') : trans('ramon-avocado.forum.home.reply_count_plural', '{count} respostas', {
      count: replies
    })))));
  }
}
flarum.reg.add('ramon-avocado', 'forum/components/shared/ThreadCard', ThreadCard);